import { Box, IconButton } from '@material-ui/core';
import { Link } from 'react-router-dom';

import { StructureCardWrapper } from '@/components/modules/MerchantDetail/MerchantStructure/components/StructureCard/StructureCardWrapper';
import { Font } from '@/components/shared/components/Font';
import { MoreActionsMenu } from '@/components/shared/components/MoreActionsMenu/MoreActionsMenu';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { SizedIconWithCircle } from '@/components/shared/components/SizedIconWithCircle';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface StructureCardProps {
  id: string;
  type: 'channel' | 'agent';
  isMobile: boolean;
  name: string;
  iconName: string;
  copyToClipboard: { value: string; textId: string };
  isSelected?: boolean;
  actions?: ActionsOption[];
  onClick?: () => void;
  redirectPath?: string;
  username?: string | null;
  badgeColor?: 'success' | 'error';
  endAdornment?: JSX.Element;
  editModeAllowed?: boolean;
}

export const StructureCard = ({
  id,
  type,
  isSelected,
  actions,
  onClick,
  redirectPath,
  name,
  username,
  iconName,
  copyToClipboard,
  badgeColor,
  isMobile,
  endAdornment,
  editModeAllowed = false,
}: StructureCardProps) => {
  const { palette } = useCustomTheme();

  return (
    <StructureCardWrapper id={`${type}-${id}`} onClick={onClick} isSelected={!!isSelected} type={type}>
      <>
        <Box display={'flex'} alignItems={'center'} flexGrow={1} className={'overflow-text-ellipsis'} pr={2}>
          <Box pr={1} flexShrink={0}>
            <Box position={'relative'}>
              {badgeColor && <Box className={'badge'} bgcolor={palette[badgeColor].main} />}
              <SizedIconWithCircle
                variant={'medium'}
                backgroundColor={'transparent'}
                iconColor={isSelected ? palette.secondary.contrastText : palette.secondary.main}
                customBorderRadius={0}
                iconName={iconName}
                copyToClipboard={copyToClipboard}
              />
            </Box>
          </Box>
          <Box
            maxWidth={'100%'}
            display={'flex'}
            flexDirection={'column'}
            textAlign={'start'}
            flexGrow={1}
            minWidth={0}
          >
            <Font
              variant={'body2'}
              className={'overflow-text-ellipsis'}
              style={{
                color: isSelected ? palette.secondary.contrastText : palette.text.title,
                wordBreak: 'break-word',
              }}
            >
              <strong>{name}</strong>
            </Font>
            <Box display={'flex'} alignItems={'center'}>
              {!isMobile && (
                <i
                  className={'material-icons-outlined'}
                  style={{
                    marginRight: 6,
                    fontSize: 14,
                    color: isSelected ? palette.secondary.contrastText : palette.text.secondary,
                  }}
                >
                  {username ? 'key' : 'key_off'}
                </i>
              )}
              <Font
                variant={'caption'}
                style={{
                  color: isSelected ? palette.secondary.contrastText : palette.text.secondary,
                  wordBreak: 'break-word',
                  //maxWidth: downXs ? '160px' : '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {username ?? <Text tId={'NO_ASSOCIATED_CHANNEL_ACCOUNT'} />}
              </Font>
            </Box>
          </Box>
        </Box>
        <Box display={'flex'} alignItems={'center'}>
          {!!endAdornment && (
            <Box
              mr={2}
              color={isSelected ? palette.secondary.contrastText : palette.text.secondary}
              display={'flex'}
              alignItems={'center'}
            >
              {endAdornment}
            </Box>
          )}
          {actions && editModeAllowed && (
            <MoreActionsMenu actionsOptionsArray={actions}>
              <IconButton
                size={'small'}
                className={'mr-2'}
                style={{ ...(isSelected && { color: palette.secondary.contrastText }) }}
              >
                {<i className={'material-icons-outlined'}>more_horiz</i>}
              </IconButton>
            </MoreActionsMenu>
          )}
          {redirectPath && (
            <Link to={redirectPath}>
              <IconButton
                size={'small'}
                className={'mr-2'}
                style={{ ...(isSelected && { color: palette.secondary.contrastText }) }}
              >
                <i className={'material-icons-outlined'}>chevron_right</i>
              </IconButton>
            </Link>
          )}
        </Box>
      </>
    </StructureCardWrapper>
  );
};
